import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OnChangeFn, SortingState, VisibilityState } from '@tanstack/react-table';
import { ColumnDef } from '@tanstack/react-table';
import Button from 'src/components/ui/Button';
import Checkbox from 'src/components/ui/Checkbox';
import {
  ArrowBendUpRightIcon,
  ArrowSquareOutIcon,
  ArrowsClockwiseIcon,
  LinkSimpleIcon,
  ListChecksIcon,
  PencilSimpleIcon,
  PencilSimpleSlashIcon,
  ShareIcon,
  SparkleIcon,
  SpinnerIcon,
  TrashIcon,
  UserMinusIcon,
  WarningIcon,
  WarningCircleIcon
} from '@phosphor-icons/react';
import Tooltip from '@/components/ui/Tooltip';
import { twMerge } from 'tailwind-merge';
import { setDragHint, subscribeDragHint } from 'src/lib/dragHint';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import TanStackTable from 'src/components/ui/TanStackTable';
import FilenameEditor from './FilenameEditor';
import { retryAnalysis } from 'src/features/wiki/api';
import type { DocumentActionReviewInfo } from 'src/features/groups/api';
import { StatutoryDocumentIcon } from 'src/features/groups/components/StatutoryComplianceSummary';
import { TagPillOverflow } from 'src/features/tags/TagPill';
import { ANALYSIS_IN_PROGRESS_STATUSES, AnalysisSummary } from '../api';
import { DocumentHubNode, isOrganisationLibraryNode, isUserFolder } from '../types';
import { DOMAIN_OPTIONS, documentTypeLabel, humaniseSlug } from '../analysisOptions';
import NodeIcon from './NodeIcon';

const HIDE_ORDER = ['tags', 'document_type', 'created_by_name', 'updated_at'] as const;
const DRAFT_FOLDER_ID = 'draft-folder';

function DragMoveHint() {
  const [label, setLabel] = useState<string | null>(null);
  const [point, setPoint] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const unsubscribe = subscribeDragHint(setLabel);
    const move = (event: DragEvent) => {
      setPoint((prev) =>
        Math.abs(prev.x - event.clientX) < 4 && Math.abs(prev.y - event.clientY) < 4
          ? prev
          : { x: event.clientX, y: event.clientY }
      );
    };
    const clear = () => setDragHint(null);
    document.addEventListener('dragover', move);
    document.addEventListener('dragend', clear);
    return () => {
      unsubscribe();
      document.removeEventListener('dragover', move);
      document.removeEventListener('dragend', clear);
    };
  }, []);
  if (!label) return null;
  return (
    <div
      className="pointer-events-none fixed z-[80] max-w-xs truncate rounded-md border border-border-subtle bg-white px-2 py-1 text-xs text-content-default shadow-md"
      style={{ left: point.x + 16, top: point.y + 20 }}
    >
      {label}
    </div>
  );
}
const COLUMN_MIN_WIDTH: Record<string, number> = {
  name: 200,
  updated_at: 160,
  document_type: 160,
  tags: 200,
  created_by_name: 160
};
const CELL_GUTTER = 32;
const ROW_ACTIONS_RESERVE = 56;

function columnMinWidth(column: { id?: string; minSize?: number; size?: number }): number {
  if (column.id && COLUMN_MIN_WIDTH[column.id] != null) return COLUMN_MIN_WIDTH[column.id];
  return column.minSize ?? column.size ?? 80;
}

function columnVisibilityForWidth(
  width: number,
  columns: { id?: string; minSize?: number; size?: number }[]
): VisibilityState {
  const columnIds = columns.map((column) => column.id).filter((id): id is string => !!id);
  const visibility: VisibilityState = {};
  for (const id of columnIds) visibility[id] = true;
  if (width <= 0) return visibility;
  const available = Math.max(0, width - ROW_ACTIONS_RESERVE);
  const byId = new Map(columns.map((column) => [column.id, column]));

  const needed = () =>
    columnIds
      .filter((id) => visibility[id] !== false)
      .reduce((sum, id) => sum + columnMinWidth(byId.get(id) ?? { id }) + CELL_GUTTER, 0);

  const extraIds = columnIds.filter(
    (id) => id !== 'select' && !(HIDE_ORDER as readonly string[]).includes(id) && id !== 'name'
  );
  for (const id of [...extraIds, ...HIDE_ORDER]) {
    if (!columnIds.includes(id)) continue;
    if (needed() <= available) break;
    visibility[id] = false;
  }
  return visibility;
}

function FileStatusIcons({ summary }: { summary: Pick<AnalysisSummary, 'has_suggestions'> | undefined }) {
  if (!summary?.has_suggestions) return null;

  return (
    <Tooltip title="AI-suggested fields awaiting review" placement="top">
      <span className="flex items-center">
        <SparkleIcon size={14} weight="light" className="text-content-subtlest shrink-0" />
      </span>
    </Tooltip>
  );
}

function ActionReviewStatusIcon({ review }: { review: DocumentActionReviewInfo }) {
  if (review.state === 'pending') {
    return (
      <Tooltip title="Extracting actions…" placement="top">
        <span className="flex items-center" aria-label="Extracting actions">
          <SpinnerIcon size={14} className="animate-spin text-secondary-blue shrink-0" />
        </span>
      </Tooltip>
    );
  }
  if (review.state === 'failed') {
    return (
      <Tooltip title="Action import failed — open to review" placement="top">
        <span className="flex items-center" aria-label="Action import failed">
          <WarningCircleIcon size={14} weight="fill" className="text-misc-error shrink-0" />
        </span>
      </Tooltip>
    );
  }
  if (review.state === 'completed') {
    return (
      <Tooltip title="Actions imported from this document" placement="top">
        <span className="flex items-center" aria-label="Actions imported">
          <ListChecksIcon size={14} className="text-green-600 shrink-0" />
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Actions ready to review" placement="top">
      <span className="flex items-center" aria-label="Actions ready to review">
        <ListChecksIcon size={14} weight="light" className="text-secondary-blue shrink-0" />
      </span>
    </Tooltip>
  );
}

const DOMAIN_LABEL = new Map<string, string>(DOMAIN_OPTIONS.map((o) => [o.value, o.label]));

function analysisSummaryFromNode(node: DocumentHubNode): AnalysisSummary | undefined {
  if (node.analysis_status === undefined) return undefined;
  return {
    analysis_status: node.analysis_status ?? null,
    error_message: null,
    tags: node.tags ?? [],
    has_suggestions: node.has_suggestions ?? false,
    expiry_status: node.expiry_status ?? null
  };
}

function formatTagValue(key: string, value: string): string {
  if (key === 'document_type') return documentTypeLabel(value);
  if (key.startsWith('domain')) return DOMAIN_LABEL.get(value) ?? humaniseSlug(value);
  if (key.startsWith('site')) return value;
  return humaniseSlug(value);
}

export function AnalysisTagsCell({
  documentId,
  summary = undefined,
  hideExpiry = false,
  show = 'all'
}: {
  documentId: string;
  summary?: AnalysisSummary | undefined;
  hideExpiry?: boolean;
  show?: 'all' | 'document_type' | 'other';
}) {
  const queryClient = useQueryClient();

  const { mutate: triggerRetry, isPending: isRetrying } = useMutation({
    mutationFn: () => retryAnalysis(documentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['folder-contents'] })
  });

  const data = summary;
  const isLoading = false;

  if (isLoading) return <span className="text-content-subtlest">—</span>;

  const showStatus = show !== 'other';
  if (showStatus && data?.analysis_status && ANALYSIS_IN_PROGRESS_STATUSES.has(data.analysis_status)) {
    return (
      <span className="flex items-center gap-1.5 text-sm italic text-content-subtlest">
        <SpinnerIcon size={16} weight="light" className="animate-spin shrink-0" />
        Analysing…
      </span>
    );
  }

  if (showStatus && data?.analysis_status === 'failed') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-red-600">
        <WarningIcon size={14} weight="fill" className="shrink-0" />
        <span>Analysis failed</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            triggerRetry();
          }}
          disabled={isRetrying}
          className="ml-1 flex items-center gap-0.5 text-red-600 hover:text-red-800 underline underline-offset-2 disabled:opacity-50"
        >
          {isRetrying ? <SpinnerIcon size={11} className="animate-spin" /> : <ArrowsClockwiseIcon size={11} />}
          Retry
        </button>
      </span>
    );
  }

  const tags: {
    key: string;
    value: string;
    confirmed: boolean;
    group?: 'document_type' | 'domain' | 'site' | 'national_returns';
    icon?: React.ReactNode;
    className?: string;
  }[] = [];

  if (!hideExpiry && show !== 'document_type') {
    if (data?.expiry_status === 'expired') {
      tags.push({
        key: 'expiry_expired',
        value: 'Expired',
        confirmed: true,
        icon: <WarningIcon size={11} weight="fill" />,
        className: 'bg-red-50 text-red-500 ring-red-200'
      });
    } else if (data?.expiry_status === 'expiring_soon') {
      tags.push({
        key: 'expiry_soon',
        value: 'Expiring soon',
        confirmed: true,
        icon: <WarningIcon size={11} weight="fill" />,
        className: 'bg-amber-50 text-amber-500 ring-amber-200'
      });
    }
  }

  for (const tag of data?.tags ?? []) {
    if (show === 'document_type' && tag.group !== 'document_type') continue;
    if (show === 'other' && tag.group === 'document_type') continue;
    tags.push({
      key: `${tag.group}:${tag.slug}`,
      value: tag.label,
      confirmed: tag.confirmed,
      group: tag.group
    });
  }

  if (!tags.length) return <span className="text-content-subtlest">—</span>;

  const overflowItems = tags.map((tag) => {
    return {
      key: tag.key,
      label: formatTagValue(tag.key, tag.value),
      group: tag.group,
      confirmed: tag.confirmed,
      icon: tag.icon,
      className: tag.className
    };
  });

  return <TagPillOverflow maxWidth="100%" items={overflowItems} />;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

interface FolderContentsProps {
  nodes: DocumentHubNode[];
  isLoading?: boolean;
  onSelect: (node: DocumentHubNode) => void;
  onOpenFolder?: (node: DocumentHubNode) => void;
  onDelete?: (node: DocumentHubNode) => void;
  onDeleteFolder?: (node: DocumentHubNode) => void;
  onUnshare?: (node: DocumentHubNode) => void;
  onMove?: (node: DocumentHubNode) => void;
  /** Drop a document onto a user folder in this list. */
  onMoveToFolder?: (documentId: string, folderId: string) => void;
  /** Drop a user folder onto another user folder in this list. */
  onMoveFolder?: (folderId: string, parentId: string) => void;
  onRename?: (node: DocumentHubNode) => void;
  /** Persist a new filename stem. The extension is kept. */
  onSaveFilename?: (node: DocumentHubNode, filename: string) => void | Promise<unknown>;
  /** Persist a new name for a user-created folder. */
  onSaveFolderName?: (node: DocumentHubNode, name: string) => void | Promise<unknown>;
  /** Show a blank folder row at the top, edited with the filename field. */
  creatingFolder?: boolean;
  onCreateFolder?: (name: string) => void | Promise<unknown>;
  onCancelCreateFolder?: () => void;
  onLinkToGroup?: (node: DocumentHubNode) => void;
  onShareWith?: (node: DocumentHubNode) => void;
  /** When provided, 'Share…' is only shown for nodes where this returns true. */
  canShareNode?: (node: DocumentHubNode) => boolean;
  /** When provided, Delete is greyed out for nodes where this returns false. */
  canDeleteNode?: (node: DocumentHubNode) => boolean;
  /** Hover text shown on a greyed-out Delete action. */
  deleteDisabledReason?: string | ((node: DocumentHubNode) => string);
  /** When provided, Move is greyed out for nodes where this returns false. */
  canMoveNode?: (node: DocumentHubNode) => boolean;
  /** Hover text shown on a greyed-out Move action. */
  moveDisabledReason?: string | ((node: DocumentHubNode) => string);
  /** When provided, Link to group is greyed out for nodes where this returns false. */
  canLinkNode?: (node: DocumentHubNode) => boolean;
  /** Hover text shown on a greyed-out Link to group action. */
  linkDisabledReason?: string | ((node: DocumentHubNode) => string);
  /**
   * When set, Move / Share / Unlink / Delete stay visible but are disabled
   * (e.g. group viewers). Hover text is this string.
   */
  writeDisabledReason?: string;
  folderReadOnly?: boolean;
  showCreatedBy?: boolean;
  /** When false, hide the Tags column even for file nodes with document ids. */
  showTags?: boolean;
  /** Server-side pagination — when provided the table renders page controls. */
  currentPage?: number;
  pageCount?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  /** Fixed row count for stable table height (matches pagination page size). */
  pageSize?: number;
  /** Show every loaded row and append more as the list scrolls. */
  infinite?: boolean;
  /** Called when the bottom of the list scrolls into view. */
  onReachEnd?: () => void;
  /**
   * The next-page fetch failed. The sentinel stays visible with hasNextPage
   * still true, so skip the observer until the user retries.
   */
  reachEndError?: boolean;
  onRetry?: () => void;
  /** A background refresh failed. Retry calls `onRefetch` rather than loading another page. */
  refetchError?: boolean;
  onRefetch?: () => void;
  isLoadingMore?: boolean;
  /** Items per page for client-side pagination (default 100). */
  clientPageSize?: number;
  /** Override the default empty-state message. */
  emptyMessage?: React.ReactNode;
  /** Initial sort order for the table. */
  initialSorting?: { id: string; desc: boolean }[];
  /** Controlled sort state (e.g. when the parent fetches a server-sorted page). */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** Skip client-side row reorder — used with server-side pagination. */
  manualSorting?: boolean;
  /**
   * Called when the table contains documents with in-progress analysis, so the
   * parent can set a short refetchInterval on its folder-contents query.
   */
  hasInProgressAnalysis?: boolean;
  /** When provided, folder nodes for which this returns true render a filled icon. */
  getNodeFilled?: (node: DocumentHubNode) => boolean;
  /** When true, skip alphabetical name sort and preserve the order nodes are passed in. */
  preserveOrder?: boolean;
  /** Additional columns appended after the built-in ones (before the actions column). */
  extraColumns?: ColumnDef<DocumentHubNode, any>[];
  /**
   * When true, rows show checkboxes and clicking selects rather than navigates.
   * `onSelectionChange` is called with the `document_id` (or `id`) of each
   * selected file row.
   */
  multiselect?: boolean;
  /** When `multiselect` is true, whether more than one file can be selected. Defaults to true. */
  enableMultiRowSelection?: boolean;
  onSelectionChange?: (documentIds: string[], nodes?: DocumentHubNode[]) => void;
  /** Document-picker mode: only the open-in-new-tab affordance on file rows. */
  pickerMode?: boolean;
  /** Document ids that are shown greyed-out and cannot be selected. */
  disabledDocumentIds?: Set<string>;
  disabledDocumentReason?: string;
  /** Optional lookup for pending action-import reviews on file rows. */
  getDocumentActionReview?: (documentId: string) => DocumentActionReviewInfo | null;
  /** When provided, linked statutory requirement names render under the file name. */
  getStatutoryDocumentLabels?: (documentId: string) => readonly string[];
  className?: string;
}

export default function FolderContents({
  nodes,
  isLoading = false,
  onSelect,
  onOpenFolder,
  onDelete,
  onUnshare,
  onMove,
  onMoveToFolder,
  onMoveFolder,
  onRename,
  onSaveFilename,
  onSaveFolderName,
  creatingFolder = false,
  onCreateFolder,
  onCancelCreateFolder,
  onDeleteFolder,
  onLinkToGroup,
  onShareWith,
  canShareNode,
  canDeleteNode,
  deleteDisabledReason,
  canMoveNode,
  moveDisabledReason,
  canLinkNode,
  linkDisabledReason,
  writeDisabledReason,
  folderReadOnly = false,
  showCreatedBy = false,
  showTags = true,
  currentPage,
  pageCount,
  totalItems,
  onPageChange,
  pageSize,
  infinite = false,
  onReachEnd,
  reachEndError = false,
  onRetry,
  refetchError = false,
  onRefetch,
  isLoadingMore = false,
  clientPageSize = 100,
  emptyMessage: emptyMessageOverride,
  initialSorting: initialSortingProp,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  manualSorting = false,
  getNodeFilled,
  preserveOrder = false,
  extraColumns,
  multiselect = false,
  enableMultiRowSelection = true,
  onSelectionChange,
  pickerMode = false,
  disabledDocumentIds,
  disabledDocumentReason,
  getDocumentActionReview,
  getStatutoryDocumentLabels,
  className
}: FolderContentsProps) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const node = tableRef.current;
    if (!node) return;
    const update = () => setTableWidth(node.offsetWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // Folders first, then files; alphabetical within each group unless preserveOrder is set
  const sorted = useMemo(() => {
    const typePriority = (node: DocumentHubNode): number => {
      if (node.folder_type === 'organisation') return 0;
      if (node.type === 'folder') return 2;
      return 3;
    };
    if (preserveOrder) {
      // Stable sort: only separate folders from files, don't reorder within each group
      return [...nodes].sort((a, b) => typePriority(a) - typePriority(b));
    }
    return [...nodes].sort((a, b) => {
      const diff = typePriority(a) - typePriority(b);
      if (diff !== 0) return diff;
      return a.name.localeCompare(b.name);
    });
  }, [nodes, preserveOrder]);

  const isDisabledFile = useCallback(
    (node: DocumentHubNode) =>
      node.type === 'file' && !!node.document_id && !!disabledDocumentIds?.has(node.document_id),
    [disabledDocumentIds]
  );

  const [internalSorting, setInternalSorting] = useState<SortingState>(initialSortingProp ?? []);
  const sorting = sortingProp ?? internalSorting;
  const setSorting = onSortingChangeProp ?? setInternalSorting;
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [pendingNames, setPendingNames] = useState<Record<string, string>>({});
  const [committedDraftName, setCommittedDraftName] = useState<string | null>(null);
  const ignoreDraftCancelRef = useRef(false);
  const draftArrived =
    committedDraftName != null && nodes.some((node) => isUserFolder(node) && node.name === committedDraftName);
  const displayed = useMemo(() => {
    if (!creatingFolder || draftArrived) return sorted;
    const draft: DocumentHubNode = {
      id: DRAFT_FOLDER_ID,
      name: committedDraftName ?? '',
      type: 'folder',
      parent_id: null
    };
    return [draft, ...sorted];
  }, [committedDraftName, creatingFolder, draftArrived, sorted]);
  useEffect(() => {
    if (!creatingFolder) setCommittedDraftName(null);
  }, [creatingFolder]);
  useEffect(() => {
    if (draftArrived) onCancelCreateFolder?.();
  }, [draftArrived, onCancelCreateFolder]);
  useEffect(() => {
    setPendingNames((current) => {
      const ids = Object.keys(current);
      if (ids.length === 0) return current;
      let changed = false;
      const next = { ...current };
      for (const id of ids) {
        if (nodes.some((node) => node.id === id && node.name === current[id])) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [nodes]);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const dragItemRef = useRef<{ kind: 'document' | 'folder'; id: string } | null>(null);
  const dragGhostRef = useRef<HTMLElement | null>(null);

  // Checkbox selection column — prepended when multiselect=true.
  // Shows "select-all" in the header (when multi-row is allowed) and a per-row
  // checkbox that appears on hover (otherwise shows the node icon).
  const selectionColumn = useMemo<ColumnDef<DocumentHubNode, any>>(
    () => ({
      id: 'select',
      size: 48,
      minSize: 48,
      maxSize: 48,
      enableSorting: false,
      enableResizing: false,
      header: ({ table }) =>
        enableMultiRowSelection ? (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={table.getIsAllRowsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                table.toggleAllRowsSelected();
              }}
            />
          </div>
        ) : null,
      cell: ({ row }) => {
        const node = row.original;
        if (node.type === 'folder' || isDisabledFile(node)) {
          return (
            <div className="flex items-center justify-center">
              <NodeIcon node={node} size={18} />
            </div>
          );
        }
        const isSelected = row.getIsSelected();
        const isHovered = hoveredRowId === row.id;
        return (
          <div
            className="flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              row.toggleSelected();
            }}
          >
            {isSelected || isHovered ? (
              <Checkbox checked={isSelected} onChange={() => row.toggleSelected()} />
            ) : (
              <NodeIcon node={row.original} size={18} />
            )}
          </div>
        );
      }
    }),
    [enableMultiRowSelection, hoveredRowId, isDisabledFile]
  );

  // Only show supplementary columns when at least one item has real data for them
  const hasDateData = useMemo(() => sorted.some((n) => !!n.updated_at), [sorted]);
  const hasTagData = useMemo(
    () => showTags && sorted.some((n) => n.type === 'file' && !!n.document_id),
    [sorted, showTags]
  );
  const hasCreatedByData = useMemo(
    () => showCreatedBy && sorted.some((n) => !!n.created_by_name),
    [sorted, showCreatedBy]
  );

  const columns = useMemo<ColumnDef<DocumentHubNode, any>[]>(() => {
    const nameCol: ColumnDef<DocumentHubNode, any> = {
      id: 'name',
      accessorKey: 'name',
      accessorFn: (row) => row.name ?? '',
      sortingFn: (rowA, rowB) =>
        (rowA.original.name ?? '').localeCompare(rowB.original.name ?? '', undefined, { sensitivity: 'base' }),
      header: 'Name',
      minSize: 200,
      cell: ({ row }) => {
        const node = row.original;
        const actionReview =
          !pickerMode && !node.uploading && node.type === 'file' && node.document_id
            ? (getDocumentActionReview?.(node.document_id) ?? null)
            : null;
        const statutoryLabels =
          !pickerMode && !node.uploading && node.type === 'file' && node.document_id
            ? (getStatutoryDocumentLabels?.(node.document_id) ?? [])
            : [];
        // Wiki titles are curated and may contain periods — don't treat them as extensions.
        // Leading-dot names (e.g. ".env") are stem-only — the only dot is at index 0.
        const lastDot = node.name.lastIndexOf('.');
        const hasExtension = node.type === 'file' && node.document_type !== 'wiki' && lastDot > 0;
        const nameStem = hasExtension ? node.name.slice(0, lastDot) : node.name;
        const nameExtension = hasExtension ? node.name.slice(lastDot + 1) : null;
        const userFolder = isUserFolder(node);
        const pendingName = pendingNames[node.id];
        const draftFolder = node.id === DRAFT_FOLDER_ID;

        return (
          <div className="flex items-center gap-2 w-full min-w-0">
            {!multiselect &&
              (node.uploading ? (
                <SpinnerIcon size={18} className="text-secondary-blue shrink-0 animate-spin" />
              ) : (
                <span data-hub-drag-icon className="inline-flex shrink-0">
                  <NodeIcon node={node} filled={getNodeFilled?.(node)} />
                </span>
              ))}
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                {(draftFolder && !committedDraftName) || renamingId === node.id ? (
                  <FilenameEditor
                    className="w-full"
                    name={draftFolder ? '' : userFolder ? node.name : nameStem}
                    extension={draftFolder || userFolder ? null : nameExtension}
                    textClassName="text-sm font-medium"
                    onSave={(filename) => {
                      if (draftFolder) {
                        ignoreDraftCancelRef.current = true;
                        setCommittedDraftName(filename);
                        void Promise.resolve(onCreateFolder?.(filename)).catch(() => {
                          setCommittedDraftName(null);
                          onCancelCreateFolder?.();
                        });
                        return;
                      }
                      const nextName = userFolder || !nameExtension ? filename : `${filename}.${nameExtension}`;
                      setPendingNames((current) => ({ ...current, [node.id]: nextName }));
                      const save = userFolder ? onSaveFolderName?.(node, filename) : onSaveFilename?.(node, filename);
                      void Promise.resolve(save).catch(() => {
                        setPendingNames((current) => {
                          if (!(node.id in current)) return current;
                          const next = { ...current };
                          delete next[node.id];
                          return next;
                        });
                      });
                    }}
                    onCancel={() => {
                      if (draftFolder) {
                        if (ignoreDraftCancelRef.current) {
                          ignoreDraftCancelRef.current = false;
                          return;
                        }
                        onCancelCreateFolder?.();
                        return;
                      }
                      setRenamingId(null);
                    }}
                  />
                ) : pendingName ? (
                  <span className="truncate">{pendingName}</span>
                ) : node.type === 'file' && hasExtension ? (
                  <span className={`truncate ${node.uploading ? 'text-content-subtlest italic' : ''}`}>
                    {nameStem}
                    <span className="text-content-subtlest">.{nameExtension}</span>
                  </span>
                ) : (
                  <span className={`truncate ${node.uploading ? 'text-content-subtlest italic' : ''}`}>
                    {node.name}
                  </span>
                )}
                {node.uploading && <span className="text-xs text-content-subtlest shrink-0">Uploading…</span>}
                {!pickerMode && !node.uploading && node.read_only && (
                  <Tooltip title="This folder is read-only" placement="top">
                    <span className="flex items-center">
                      <PencilSimpleSlashIcon size={12} className="text-content-subtlest shrink-0" weight="regular" />
                    </span>
                  </Tooltip>
                )}
                {!pickerMode && !node.uploading && node.type === 'file' && node.document_id && (
                  <FileStatusIcons
                    summary={node.has_suggestions != null ? { has_suggestions: node.has_suggestions } : undefined}
                  />
                )}
                {actionReview && <ActionReviewStatusIcon review={actionReview} />}
                {node.group_relation === 'linked' && (
                  <Tooltip title="This file lives in the Organisation Folder and is linked here" placement="top">
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-100 shrink-0">
                      <LinkSimpleIcon size={12} weight="light" />
                      Linked
                    </span>
                  </Tooltip>
                )}
                {pickerMode && !node.uploading && node.document_id && (
                  <Tooltip title="Open in new tab" placement="top">
                    <a
                      href={`/wiki/${node.document_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-content-subtlest hover:text-content-default transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ArrowSquareOutIcon size={13} />
                    </a>
                  </Tooltip>
                )}
              </div>
              {statutoryLabels.length > 0 ? (
                <span className="inline-flex min-w-0 items-center gap-1 text-xs text-content-subtle truncate">
                  <StatutoryDocumentIcon size={12} className="shrink-0" />
                  <span className="truncate">{statutoryLabels.join(', ')}</span>
                </span>
              ) : (
                node.subtitle && <span className="text-xs text-content-subtlest truncate">{node.subtitle}</span>
              )}
            </div>
          </div>
        );
      }
    };

    const restCols: ColumnDef<DocumentHubNode, any>[] = [
      ...(hasDateData
        ? [
            {
              id: 'updated_at',
              accessorKey: 'updated_at',
              accessorFn: (row: DocumentHubNode) => row.updated_at ?? '',
              sortingFn: (rowA: { original: DocumentHubNode }, rowB: { original: DocumentHubNode }) => {
                const a = rowA.original.updated_at ? new Date(rowA.original.updated_at).getTime() : 0;
                const b = rowB.original.updated_at ? new Date(rowB.original.updated_at).getTime() : 0;
                return a - b;
              },
              header: 'Date modified',
              size: 160,
              minSize: 160,
              enableResizing: false,
              cell: ({ row }: any) => <span className="text-content-subtle">{formatDate(row.original.updated_at)}</span>
            } as ColumnDef<DocumentHubNode, any>
          ]
        : []),
      ...(hasTagData
        ? [
            {
              id: 'document_type',
              header: 'Document type',
              size: 160,
              enableResizing: false,
              cell: ({ row }: any) => {
                const node: DocumentHubNode = row.original;
                if (node.type === 'folder' || !node.document_id) {
                  return <span className="text-content-subtlest">—</span>;
                }
                return (
                  <AnalysisTagsCell
                    documentId={node.document_id}
                    summary={analysisSummaryFromNode(node)}
                    show="document_type"
                  />
                );
              }
            } as ColumnDef<DocumentHubNode, any>,
            {
              id: 'tags',
              header: 'Tags',
              size: 200,
              enableResizing: false,
              cell: ({ row }: any) => {
                const node: DocumentHubNode = row.original;
                if (node.type === 'folder' || !node.document_id) {
                  return <span className="text-content-subtlest">—</span>;
                }
                return (
                  <AnalysisTagsCell
                    documentId={node.document_id}
                    summary={analysisSummaryFromNode(node)}
                    show="other"
                  />
                );
              }
            } as ColumnDef<DocumentHubNode, any>
          ]
        : []),
      ...(hasCreatedByData
        ? [
            {
              id: 'created_by_name',
              accessorKey: 'created_by_name',
              header: 'Created By',
              size: 160,
              enableResizing: false,
              cell: ({ row }: any) => (
                <span className="text-content-subtle truncate">{row.original.created_by_name ?? '—'}</span>
              )
            } as ColumnDef<DocumentHubNode, any>
          ]
        : [])
    ];

    return [nameCol, ...restCols, ...(extraColumns ?? [])];
  }, [
    onOpenFolder,
    onDelete,
    hasDateData,
    hasTagData,
    hasCreatedByData,
    getNodeFilled,
    extraColumns,
    multiselect,
    pickerMode,
    getDocumentActionReview,
    getStatutoryDocumentLabels,
    renamingId,
    onSaveFilename,
    pendingNames,
    committedDraftName,
    onCreateFolder,
    onCancelCreateFolder
  ]);

  // Prepend checkbox selection column in multiselect mode
  const finalColumns = useMemo(
    () => (multiselect ? [selectionColumn, ...columns] : columns),
    [multiselect, selectionColumn, columns]
  );

  const actions = useMemo(
    () =>
      [
        {
          label: 'Rename',
          icon: PencilSimpleIcon,
          onClick: (node: DocumentHubNode) => {
            setRenamingId(node.id);
            onRename?.(node);
          },
          hidden: (node: DocumentHubNode) =>
            isUserFolder(node)
              ? !onSaveFolderName
              : !onSaveFilename ||
                node.type === 'folder' ||
                !node.document_id ||
                node.document_type === 'canvas' ||
                node.document_type === 'wiki',
          disabled: (node: DocumentHubNode) =>
            !!node.uploading ||
            !!writeDisabledReason ||
            !!node.read_only ||
            !!folderReadOnly ||
            (!isUserFolder(node) && !!canMoveNode && !canMoveNode(node)),
          disabledTitle: writeDisabledReason || moveDisabledReason
        },
        {
          label: 'Move\u2026',
          icon: ArrowBendUpRightIcon,
          onClick: onMove ?? (() => {}),
          hidden: (node: DocumentHubNode) =>
            !onMove ||
            (isUserFolder(node)
              ? false
              : node.type === 'folder' || !node.document_id || node.document_type === 'canvas'),
          disabled: (node: DocumentHubNode) =>
            !!node.uploading ||
            !!writeDisabledReason ||
            !!folderReadOnly ||
            !!node.read_only ||
            (!isUserFolder(node) && (canMoveNode ? !canMoveNode(node) : false)),
          disabledTitle: writeDisabledReason || moveDisabledReason
        },
        {
          label: 'Link to group\u2026',
          icon: LinkSimpleIcon,
          onClick: onLinkToGroup ?? (() => {}),
          hidden: (node: DocumentHubNode) =>
            !onLinkToGroup || node.type === 'folder' || !node.document_id || !isOrganisationLibraryNode(node),
          disabled: (node: DocumentHubNode) =>
            !!node.uploading || !!writeDisabledReason || (canLinkNode ? !canLinkNode(node) : false),
          disabledTitle: writeDisabledReason || linkDisabledReason
        },
        {
          label: 'Share\u2026',
          icon: ShareIcon,
          onClick: onShareWith ?? (() => {}),
          hidden: (node: DocumentHubNode) =>
            !onShareWith || node.type === 'folder' || (canShareNode ? !canShareNode(node) : false),
          disabled: (node: DocumentHubNode) => !!node.uploading || !!writeDisabledReason || !!folderReadOnly,
          disabledTitle: writeDisabledReason
        },
        {
          label: 'Unlink',
          icon: UserMinusIcon,
          onClick: onUnshare ?? (() => {}),
          className: 'text-red-600',
          hidden: (node: DocumentHubNode) => !onUnshare || node.type === 'folder' || node.group_relation !== 'linked',
          disabled: (node: DocumentHubNode) => !!node.uploading || !!writeDisabledReason,
          disabledTitle: writeDisabledReason
        },
        {
          label: 'Delete',
          icon: TrashIcon,
          onClick: (node: DocumentHubNode) => (isUserFolder(node) ? onDeleteFolder?.(node) : onDelete?.(node)),
          className: 'text-red-600',
          hidden: (node: DocumentHubNode) =>
            isUserFolder(node)
              ? !onDeleteFolder
              : !onDelete ||
                node.type === 'folder' ||
                (!writeDisabledReason && folderReadOnly) ||
                node.group_relation === 'linked',
          disabled: (node: DocumentHubNode) =>
            !!node.uploading ||
            !!writeDisabledReason ||
            !!node.read_only ||
            (isUserFolder(node) ? false : canDeleteNode ? !canDeleteNode(node) : false),
          disabledTitle: writeDisabledReason || deleteDisabledReason
        }
      ].map((action) => ({
        ...action,
        hidden: (node: DocumentHubNode) => node.id === DRAFT_FOLDER_ID || action.hidden(node)
      })),
    [
      onDelete,
      onUnshare,
      onMove,
      onSaveFilename,
      onSaveFolderName,
      onDeleteFolder,
      onLinkToGroup,
      onShareWith,
      canShareNode,
      canDeleteNode,
      deleteDisabledReason,
      canMoveNode,
      moveDisabledReason,
      canLinkNode,
      linkDisabledReason,
      writeDisabledReason,
      folderReadOnly
    ]
  );

  const columnVisibility = useMemo(() => {
    return columnVisibilityForWidth(tableWidth, finalColumns);
  }, [finalColumns, tableWidth]);

  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const target = endRef.current;
    if (!target || !infinite || !onReachEnd || reachEndError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReachEnd();
      },
      // The page scrolls, not this list, so watch the viewport.
      { root: null, rootMargin: '240px' }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [infinite, onReachEnd, reachEndError, sorted.length]);

  const listPageSize = infinite ? Math.max(sorted.length, 1) : pageSize;

  return (
    <div
      ref={tableRef}
      className={twMerge(pickerMode ? '' : infinite ? 'min-w-0' : 'overflow-y-auto h-full min-w-0', className)}
    >
      <DragMoveHint />
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-content-subtlest gap-2">
          <SpinnerIcon size={16} className="animate-spin" />
          Loading…
        </div>
      ) : (
        <TanStackTable
          data={displayed}
          columns={finalColumns}
          actions={multiselect ? [] : actions}
          contextMenu={!multiselect}
          // In multiselect mode, row clicks toggle selection rather than navigate
          onRowClick={(node) => {
            if (node.id === DRAFT_FOLDER_ID) return;
            if (node.type === 'folder') {
              if (onOpenFolder) onOpenFolder(node);
              else onSelect(node);
              return;
            }
            if (!multiselect) onSelect(node);
          }}
          getRowId={(row) => (multiselect ? (row.document_id ?? row.id) : row.id)}
          multiselect={multiselect}
          enableMultiRowSelection={enableMultiRowSelection}
          isRowSelectable={(node) => node.type === 'file' && !isDisabledFile(node)}
          rowTitle={(node) => (isDisabledFile(node) ? (disabledDocumentReason ?? 'Already in this group') : '')}
          getRowProps={(node) => {
            if (node.id === DRAFT_FOLDER_ID) return {};
            if (isDisabledFile(node)) return { className: 'opacity-50 cursor-not-allowed' };
            const canDragFile = !!onMoveToFolder && node.type === 'file' && !!node.document_id && !node.uploading;
            const canDragFolder = !!onMoveFolder && isUserFolder(node) && !node.read_only;
            const canDrag = canDragFile || canDragFolder;
            const canReceive = isUserFolder(node) && !node.read_only && (!!onMoveToFolder || !!onMoveFolder);
            return {
              draggable: canDrag || undefined,
              className: dropTargetId === node.id ? 'bg-blue-50' : undefined,
              onDragStart: canDrag
                ? (event: React.DragEvent) => {
                    const drag = canDragFolder
                      ? { kind: 'folder' as const, id: node.id }
                      : { kind: 'document' as const, id: node.document_id ?? '' };
                    dragItemRef.current = drag;
                    event.dataTransfer.setData(
                      drag.kind === 'folder' ? 'application/x-hub-folder' : 'application/x-hub-document',
                      drag.id
                    );
                    event.dataTransfer.setData('text/plain', drag.id);
                    event.dataTransfer.effectAllowed = 'move';
                    dragGhostRef.current?.remove();
                    const ghost = document.createElement('div');
                    ghost.style.position = 'absolute';
                    ghost.style.top = '-1000px';
                    ghost.style.left = '0';
                    ghost.style.display = 'flex';
                    ghost.style.alignItems = 'center';
                    ghost.style.gap = '8px';
                    ghost.style.maxWidth = '280px';
                    ghost.style.padding = '6px 10px';
                    ghost.style.background = '#fff';
                    ghost.style.border = '1px solid #e5e7eb';
                    ghost.style.borderRadius = '6px';
                    ghost.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                    const rowFont = getComputedStyle(event.currentTarget);
                    ghost.style.fontSize = rowFont.fontSize;
                    ghost.style.fontFamily = rowFont.fontFamily;
                    ghost.style.fontWeight = rowFont.fontWeight;
                    ghost.style.lineHeight = rowFont.lineHeight;
                    ghost.style.color = rowFont.color;
                    const icon = event.currentTarget.querySelector('[data-hub-drag-icon] svg');
                    if (icon) ghost.appendChild(icon.cloneNode(true));
                    const label = document.createElement('span');
                    label.textContent = node.name;
                    label.style.overflow = 'hidden';
                    label.style.textOverflow = 'ellipsis';
                    label.style.whiteSpace = 'nowrap';
                    ghost.appendChild(label);
                    document.body.appendChild(ghost);
                    dragGhostRef.current = ghost;
                    event.dataTransfer.setDragImage(ghost, 16, 16);
                  }
                : undefined,
              onDragEnd: canDrag
                ? () => {
                    dragItemRef.current = null;
                    dragGhostRef.current?.remove();
                    dragGhostRef.current = null;
                    setDragHint(null);
                    setDropTargetId(null);
                  }
                : undefined,
              onDragOver: canReceive
                ? (event: React.DragEvent) => {
                    const drag = dragItemRef.current;
                    if (!drag || drag.id === node.id || event.dataTransfer.types.includes('Files')) return;
                    if (drag.kind === 'document' && !onMoveToFolder) return;
                    if (drag.kind === 'folder' && !onMoveFolder) return;
                    const source =
                      drag.kind === 'folder'
                        ? sorted.find((row) => row.id === drag.id)
                        : sorted.find((row) => row.document_id === drag.id);
                    if (source?.parent_id === node.id) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    setDropTargetId((current) => (current === node.id ? current : node.id));
                    setDragHint(`Move to ${node.name}`);
                  }
                : undefined,
              onDragLeave: canReceive
                ? () => {
                    setDropTargetId((current) => (current === node.id ? null : current));
                    setDragHint(null);
                  }
                : undefined,
              onDrop: canReceive
                ? (event: React.DragEvent) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDropTargetId(null);
                    setDragHint(null);
                    const folderId = event.dataTransfer.getData('application/x-hub-folder');
                    if (folderId) {
                      if (folderId === node.id) return;
                      const source = sorted.find((row) => row.id === folderId);
                      if (source?.parent_id === node.id) return;
                      onMoveFolder?.(folderId, node.id);
                      return;
                    }
                    const documentId = event.dataTransfer.getData('application/x-hub-document');
                    if (!documentId) return;
                    const source = sorted.find((row) => row.document_id === documentId);
                    if (source?.parent_id === node.id) return;
                    onMoveToFolder?.(documentId, node.id);
                  }
                : undefined
            };
          }}
          onSelectionChange={
            multiselect && onSelectionChange
              ? (ids) => {
                  const documentIds = ids.map(String);
                  const byId = new Map(
                    sorted
                      .filter((node) => node.type === 'file')
                      .map((node) => [String(node.document_id ?? node.id), node])
                  );
                  onSelectionChange(
                    documentIds,
                    documentIds.flatMap((id) => {
                      const node = byId.get(id);
                      return node ? [node] : [];
                    })
                  );
                }
              : undefined
          }
          onHoveredRowChange={multiselect ? (id) => setHoveredRowId(id != null ? String(id) : null) : undefined}
          fluidColumnId="name"
          columnVisibility={columnVisibility}
          enableResizing={false}
          enableSorting
          stickyHeader={infinite}
          manualSorting={manualSorting}
          sorting={sorting}
          onSortingChange={setSorting}
          itemsPerPage={
            infinite ? listPageSize : pageCount !== undefined ? (pageSize ?? clientPageSize) : clientPageSize
          }
          headerSize="comfortable"
          minHeight={
            infinite || pageSize === undefined
              ? undefined
              : ((pickerMode ? pageSize : Math.min(Math.max(sorted.length, 1), pageSize)) + 1) * 48
          }
          emptyMessage={
            emptyMessageOverride ?? (
              <div className="flex flex-col items-center gap-1 py-4 text-content-subtlest text-sm">
                <span>This folder is empty</span>
                {!folderReadOnly && <span>Try uploading a file to get started</span>}
              </div>
            )
          }
          {...(infinite
            ? { currentPage: 1, pageCount: 1, onPageChange: () => {} }
            : pageCount !== undefined && onPageChange && currentPage !== undefined
              ? { currentPage, pageCount, totalItems, onPageChange }
              : {})}
        />
      )}
      {infinite && sorted.length > 0 && (
        <div ref={endRef} className="flex justify-center py-3 text-sm text-content-subtlest">
          {isLoadingMore ? (
            <span className="inline-flex items-center gap-2">
              <SpinnerIcon size={16} className="animate-spin" />
              Loading…
            </span>
          ) : reachEndError && onReachEnd ? (
            <Button variant="outline" size="sm" onClick={() => onRetry?.()}>
              Retry
            </Button>
          ) : refetchError ? (
            <Button variant="outline" size="sm" onClick={() => onRefetch?.()}>
              Retry
            </Button>
          ) : !onReachEnd ? (
            <span>End of list</span>
          ) : null}
        </div>
      )}
    </div>
  );
}
